import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ContactV2, PersonV2 } from "interfaces/db";
import { getServicesManager } from "services";
import { Dispatch } from 'redux';
import { createSelector } from '@reduxjs/toolkit';

interface EnrichState {
  loading: boolean,
  error: string | null,
}

interface EnrichContact extends EnrichState {
  contacts: ContactV2[] | null,
}

interface EnrichPerson extends EnrichState {
  persons: PersonV2[],
}
        
export type EnrichStateResult = {
    numberToContact: {[number: string]: EnrichContact},
    platformPersonIdToPerson: {[platformPersonId: string]: EnrichPerson},
} 

export const initialEnrichStateResult: EnrichStateResult = {
    numberToContact: {},
    platformPersonIdToPerson: {},
}

export const enrichSlice = createSlice({
    name: 'enrich',
    initialState: initialEnrichStateResult,
    reducers: {
        setContactLoading: (state, action: PayloadAction<{ number: string, loading: boolean }>) => {
            const { number, loading } = action.payload;
            if (!state.numberToContact[number]) {
                state.numberToContact[number] = { loading, error: null, contacts: null };
            } else {
                state.numberToContact[number].loading = loading;
            }
        },
        setContactError: (state, action: PayloadAction<{ number: string, error: string }>) => {
            const { number, error } = action.payload;
            if (!state.numberToContact[number]) {
                state.numberToContact[number] = { loading: false, error, contacts: null };
            } else {
                state.numberToContact[number].error = error;
            }
        },
        setContacts: (state, action: PayloadAction<{ number: string, contacts: ContactV2[] }>) => {
            const { number, contacts } = action.payload;
            if (!state.numberToContact[number]) {
                state.numberToContact[number] = { loading: false, error: null, contacts };
            } else {
                state.numberToContact[number].contacts = contacts;
            }
        },
        setPersonLoading: (state, action: PayloadAction<{ platformPersonId: string, loading: boolean }>) => {
            const { platformPersonId, loading } = action.payload;
            if (!state.platformPersonIdToPerson[platformPersonId]) {
                state.platformPersonIdToPerson[platformPersonId] = { loading, error: null, persons: [] };
            } else {
                state.platformPersonIdToPerson[platformPersonId].loading = loading;
            }
        },
        setPersonError: (state, action: PayloadAction<{ platformPersonId: string, error: string }>) => {
            const { platformPersonId, error } = action.payload;
            if (!state.platformPersonIdToPerson[platformPersonId]) {
                state.platformPersonIdToPerson[platformPersonId] = { loading: false, error, persons: [] };
            } else {
                state.platformPersonIdToPerson[platformPersonId].error = error;
            }
        },
        setPersons: (state, action: PayloadAction<{ platformPersonId: string, persons: PersonV2[] }>) => {
            const { platformPersonId, persons } = action.payload;
            if (!state.platformPersonIdToPerson[platformPersonId]) {
                state.platformPersonIdToPerson[platformPersonId] = { loading: false, error: null, persons };
            } else {
                state.platformPersonIdToPerson[platformPersonId].persons = persons;
            }
        },
    },
});

export const {
    setContactLoading,
    setContactError,
    setContacts,
    setPersonLoading,
    setPersonError,
    setPersons,
} = enrichSlice.actions;

// Selectors
export const selectContactByNumber = (state: { enrich: EnrichStateResult }, number: string) => 
    state.enrich.numberToContact[number]?.contacts;

export const selectPersonByNumber = createSelector(
    [(state: { enrich: EnrichStateResult }) => state.enrich.numberToContact,
     (state: { enrich: EnrichStateResult }) => state.enrich.platformPersonIdToPerson,
     (_, number: string) => number],
    (numberToContact, platformPersonIdToPerson, number) => {
        const contacts = numberToContact[number]?.contacts;
        if (!contacts) return null;

        // Get all platform person IDs from contacts
        const platformPersonIds = contacts
            .map(contact => contact.platform_person_id)
            .filter((id): id is string => id !== null && id !== undefined);

        // Get the first person found for any of these IDs
        for (const platformPersonId of platformPersonIds) {
            const person = platformPersonIdToPerson[platformPersonId]?.persons[0];
            if (person) return person;
        }

        return null;
    }
);

export async function enrichContacts(
    all_addresses: string[],
    dispatch: Dispatch,
) {
    const servicesManager = getServicesManager();
    const addresses = Array.from(new Set(all_addresses.filter((address): address is string => address !== null && address !== undefined)));
    
    // Set loading state for all addresses
    addresses.forEach(address => {
        dispatch(setContactLoading({ number: address, loading: true }));
    });

    try {
        // First enrich to contacts
        const contacts = await servicesManager.enrichContacts(addresses);
        if (!contacts) {
            throw new Error('Failed to enrich contacts');
        }

        // Update contacts in store for each address
        const addressToContacts = new Map<string, ContactV2[]>();
        addresses.forEach(address => {
            const addressContacts = contacts.filter(contact => contact.address === address);
            // Take the first contact if there are multiple matches
            addressToContacts.set(address, addressContacts.slice(0, 1));
            dispatch(setContacts({ number: address, contacts: addressContacts.slice(0, 1) }));
            dispatch(setContactLoading({ number: address, loading: false }));
        });
        const uniqueContacts = Array.from(addressToContacts.values()).flat();

        // Then enrich to persons
        const persons = await servicesManager.enrichContactsToPerson(uniqueContacts);
        if (!persons) {
            throw new Error('Failed to enrich persons');
        }

        // Update persons in store for each platform person ID
        persons.forEach(person => {
            if (person.platform_person_id) {
                dispatch(setPersons({ platformPersonId: person.platform_person_id, persons: [person] }));
            }
        });

        return {
            contacts,
            persons
        };
    } catch (error) {
        // Set error state for all addresses
        addresses.forEach(address => {
            dispatch(setContactError({ number: address, error: error instanceof Error ? error.message : 'Unknown error' }));
            dispatch(setContactLoading({ number: address, loading: false }));
        });
        throw error;
    }
}

